import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Connection } from '../../../../../models/connection.model';
import { Component as ComponentModel } from '../../../../../models/component.model';
import { App } from '../../../../../shared/enums/app/app.module';
import { Direction } from '../../../../../shared/enums/direction/direction.module';
import { UtilsService } from '../../../../../services/shared/utils/utils.service';
import { TriggerType } from '../../../../../shared/enums/trigger-type/trigger-type.module';
import { TranslationService } from '../../../../../services/translation/translation.service';
import { ConnectionDefinition } from '../../../../../models/connectionDefinition.model';
import { ComponentType } from '../../../../../shared/enums/component-type/component-type.module';
import { DialogService } from '../../../../../services/shared/dialog/dialog.service';
import { filter } from 'rxjs';

@Component({
  selector: 'app-connection',
  templateUrl: './connection.component.html',
  styleUrl: './connection.component.css'
})

export class ConnectionComponent implements OnInit {
  @Input() connection!: Connection;
  @Input() component!: ComponentModel;
  @Input() fullConnectionsDefinitions!: ConnectionDefinition[];
  @Output() removeConnection = new EventEmitter<void>();
  @Output() updateSelectOptions = new EventEmitter<string|undefined>();
  @Output() onConnectionBuilded = new EventEmitter<string|undefined>();

  Object = Object;

  App = App;
  Direction = Direction;

  appSelected : App | undefined = undefined;
  tagSelected : string[] = []; //connection values ids

  constructor(
    public utils: UtilsService,
    public translationService: TranslationService,
    public dialogService: DialogService) { }

  directionTypes = Object.keys(Direction)
  .filter(key => isNaN(Number(key)))
  .map(key => ({ key, value: Direction[key as keyof typeof Direction] }));

  triggerTypes = Object.keys(TriggerType)
  .filter(key => isNaN(Number(key)))
  .map(key => ({ key, value: TriggerType[key as keyof typeof TriggerType] }));

  availableConnectionsDefinitions: ConnectionDefinition[] = [];
  availableApps: { [id: string]: string } = {}; //id / display
  availableTags: { [id: string]: string } = {}; //id / display
  availableDirections: {
      key: string;
      value: Direction;
  }[] = [];
    availableTriggers: {
      key: string;
      value: TriggerType;
  }[] = [];

  needSubType: boolean = false;

  currentDefinition: ConnectionDefinition | undefined = undefined;

  ComponentType = ComponentType;
  ngOnInit(){
    this.updateAvailableConnectionsDefinitions();
    this.updateAvailableApps();

    this.tagSelected = this.connection.connectionDefinition?.values ? this.connection.connectionDefinition?.values.map(v => v.id) : [];

    this.currentDefinition = this.availableConnectionsDefinitions.find(def =>
    this.connection.connectionDefinition?.id == def.id
    && (def.values == undefined
      || (this.tagSelected.length > 0
        && def.values.some(val => this.tagSelected.includes(val.id) && (!this.needSubType || this.component.config?.contentAPISubType != undefined)))));

    if(this.currentDefinition)
    {
      this.appSelected = this.currentDefinition?.app;

      if (this.appSelected != null && this.currentDefinition.values != undefined) {
        this.updateAvailableTags(this.appSelected);
      }
    }
    else if(this.availableConnectionsDefinitions.length == 1 && this.availableConnectionsDefinitions[0].app != undefined) //autocomplete if only one option
    {
      this.onAppChange(this.availableConnectionsDefinitions[0].app.toString());
    }

    this.needSubType = this.appSelected == App.CONTENT && (this.component.type == ComponentType.URL || this.component.type == ComponentType.VISUAL_PUBLICATION);

    this.updateAvailableDirections();
  }

  updateAvailableConnectionsDefinitions() {
    const isNullOrUndefined = (val: any) => val === undefined || val === null;

    const isMatchingComponent = (x: any) => {
      const comp = this.component;
      return (
        x.type === comp.type &&
        (isNullOrUndefined(comp.config?.inputType) || isNullOrUndefined(x.inputType) || comp.config?.inputType === x.inputType) &&
        (isNullOrUndefined(comp.config?.selectType) || isNullOrUndefined(x.selectType) || comp.config?.selectType === x.selectType)
      );
    };

    var currentDefinitions = this.component.connections?.filter(c => c.id != this.connection.id)?.flatMap(c => c.connectionDefinition);
    var excludedTagDefinitionsIds = currentDefinitions?.flatMap(def => def?.values)?.flatMap(val => val?.id);

    this.availableConnectionsDefinitions = this.fullConnectionsDefinitions
      .filter(def => !def.values || def.values.some(val =>
        val.includedComponents?.some(isMatchingComponent) &&
        !val.excludedComponents?.some(isMatchingComponent)
      ))
      .map(def => ({
        id: def.id,
        app: def.app,
        endpoints: def.endpoints,
        values: def.values
          ? def.values.filter(val =>
              val.includedComponents?.some(isMatchingComponent) &&
              !val.excludedComponents?.some(isMatchingComponent) &&
              (!excludedTagDefinitionsIds || !excludedTagDefinitionsIds.includes(val.id))
            )
          : undefined
    }));

    this.availableConnectionsDefinitions = this.availableConnectionsDefinitions.filter(def => (!def.values && def.app == App.CDC) || (def.values && def.values.length > 0));
  }

  updateAvailableApps(): void{
    this.availableApps = this.availableConnectionsDefinitions
    .reduce((acc, def) => {
      const appId = def.app as number;
      const appName = App[appId];
      if (appName) {
        acc[appId] = appName;
      }
      return acc;
    }, {} as { [id: string]: string });
  }

  updateAvailableTags(selectedApp: App): void {
    this.availableTags = this.availableConnectionsDefinitions
    .filter(def => def.app === selectedApp)
    .reduce((acc, def) => {
      (def.values || []).forEach(value => {
        if (value.id && value.name) {
          acc[value.id] = value.name;
        }
      });
      return acc;
    }, {} as { [id: string]: string });
  }

  updateAvailableDirections(): void {
    if (this.component.type === ComponentType.VISUAL_PUBLICATION)
    {
      this.availableDirections = this.directionTypes.filter(x => x.value !== Direction.IN);
    }
    else if (this.component.type === ComponentType.ACTIVITY_PRESTATIONS
      || this.component.type === ComponentType.SCHEDULE
      || this.component.type === ComponentType.URL
      || (this.component.type === ComponentType.SELECT && this.tagSelected.some(id => this.availableTags[id]?.toLowerCase() === 'certification')))
    {
      this.availableDirections = this.directionTypes.filter(x => x.value !== Direction.OUT);
    }
    else if (this.component.type === ComponentType.ADDRESS || this.component.type === ComponentType.INTITULE)
      {
      this.availableDirections = this.directionTypes.filter(x => x.value === Direction.IN);
    }
    else{
      this.availableDirections = this.directionTypes;
    }

    this.updateFilteredTriggers();
  }

  updateFilteredTriggers() {
    if (this.connection.direction === undefined || this.connection.direction === null) {
      this.availableTriggers = [];
      return;
    }

    this.availableTriggers = this.triggerTypes.filter(trigger => {
      if (this.connection.direction === Direction.IN) return true;
      if (this.connection.direction === Direction.OUT || this.connection.direction === Direction.IN_OUT) {
        return trigger.value === TriggerType.DYNAMIC;
      }
      return true;
    });

    // Auto-selection
    const isCurrentValid = this.availableTriggers.some(t => t.value === this.connection.trigger);
    if (!isCurrentValid) {
      this.connection.trigger = this.availableTriggers.length === 1
        ? this.availableTriggers[0].value
        : undefined;
    }
  }

  onRemoveConnection(event: Event) {
    event.stopPropagation();
    this.removeConnection.emit();
  }

  onAppChange(appSelected: string) {
    this.appSelected = parseInt(appSelected);
    this.tagSelected = [];
    this.connection.connectionDefinition = undefined;

    this.needSubType = this.appSelected == App.CONTENT && (this.component.type == ComponentType.URL || this.component.type == ComponentType.VISUAL_PUBLICATION);

    if(this.appSelected === App.CDC)
    {
      this.buildConnectionDefinition();
      this.connection.direction = Direction.IN_OUT;
      this.connection.trigger = TriggerType.STATIC;
    }
    else{
      this.updateAvailableTags(this.appSelected);

      if(this.availableTags != undefined && Object.keys(this.availableTags).length == 1)
      {
        this.tagSelected = this.component.type != ComponentType.ACTIVITY_PRESTATIONS ? [Object.entries(this.availableTags)[0][0]] : []; //id
        this.onTagChange();
      }

      if(this.appSelected === App.BUS_FLASH)
      {
        this.connection.direction = Direction.OUT;
        this.connection.trigger = TriggerType.DYNAMIC;
      }
      else if(this.appSelected === App.CONTENT)
      {
        this.connection.direction = this.component.type == ComponentType.ADDRESS || this.component.type == ComponentType.INTITULE ? Direction.IN : Direction.IN_OUT;
        this.connection.trigger = TriggerType.DYNAMIC;
      }
      else if(this.appSelected === App.CUSTOMER_PROFILE)
      {
        this.connection.direction = Direction.IN_OUT;
        this.connection.trigger = TriggerType.DYNAMIC;
      }
      else if(this.appSelected === App.ICARD)
      {
        this.connection.direction = Direction.IN;
        this.connection.trigger = TriggerType.DYNAMIC;
      }
      else if(this.appSelected === App.SOPROD)
      {
        this.connection.direction = Direction.IN;
        this.connection.trigger = TriggerType.DYNAMIC;
      }
      else
      {
        this.connection.direction = Direction.IN;
        this.connection.trigger = TriggerType.STATIC;
      }
    }

    this.updateAvailableDirections();
  }

  onTagChange() {
    if(this.appSelected && this.tagSelected)
    {
      if (typeof this.tagSelected === 'string') {
        this.tagSelected = [this.tagSelected];
      }
      if(!this.evaluateTag())
      {
        return;
      }
    }

    if(!this.needSubType || this.component.config?.contentAPISubType != undefined)
    {
      this.buildConnectionDefinition();
    }

    this.updateAvailableDirections();
  }

  onTypeChange() {
    this.buildConnectionDefinition();
  }

  buildConnectionDefinition()
  {
    const currentDefinition = this.availableConnectionsDefinitions.find(
      x => x.app === this.appSelected
      && (!x.values || x.values?.some(y => this.tagSelected.includes(y.id) && (!this.needSubType || this.component.config?.contentAPISubType != undefined)))
    );

    if (currentDefinition) {
      var currentValues = currentDefinition?.values?.filter(x => this.tagSelected.includes(x.id));

      if(this.component.config?.contentAPISubType && this.component.config?.contentAPISubType == "LOGO" && currentValues?.some(cv => cv.handle))
      {
        currentValues[0].handle = "SINGLE";
      }

      this.connection.connectionDefinition = {
        id: currentDefinition.id,
        app: currentDefinition.app,
        endpoints: currentDefinition.endpoints,
        ...(currentDefinition.app !== App.CDC && {
          values: currentValues?.map(v => ({
            id: v.id,
            handle: v.handle,
            operations: v.operations
          }))
        })
      };

      this.onConnectionBuilded.emit(currentValues?.[0]?.handle);
    }
  }

  onDirectionChange(selectedValue: string) {
    this.connection.direction = parseInt(selectedValue, 10);
    this.updateFilteredTriggers();

    if(this.component.type == ComponentType.ACTIVITY_PRESTATIONS){
      if(this.connection.direction == Direction.IN_OUT){
        this.connection.trigger = TriggerType.DYNAMIC;
      }
      else if(this.connection.direction == Direction.IN){
        this.connection.trigger = TriggerType.UNTIL_CHANGE;
      }
    }
  }

  onTriggerChange(selectedValue: string) {
    this.connection.trigger = parseInt(selectedValue, 10);
  }

  evaluateTag(): boolean
  {
    if(this.component.type == ComponentType.SELECT)
    {
      var currentDefinitionName = this.availableConnectionsDefinitions.find(x => x.app == App.CONTENT)?.values?.find(y => y.id == this.tagSelected[0])?.name;
      if(currentDefinitionName == 'certification')
      {
        if(this.component.config?.options && this.component.config?.options.length > 0)
        {
          this.dialogService.confirm(
            this.translationService.translate('Change connection tag?'),
            this.translationService.translate('This will remove your current select options.') + this.translationService.translate('Are you sure you want to continue?')
          ).then((confirmed) => {
            if (confirmed) {
              this.updateSelectOptions.emit(currentDefinitionName);
              return true;
            }
            else{
              this.tagSelected = [];
              return false;
            }
          });
        }
        else
        {
          this.updateSelectOptions.emit(currentDefinitionName);
        }
      }
    }
    return true;
  }
}
